import { nanoid } from 'nanoid'
import { computed } from 'vue'
import { useState } from '#app/composables/state'
import type { RouteLocationRaw } from '#vue-router'
import type { ToastType } from '~/composables'

export const DEFAULT_NOTIFICATION_DURATION = 5000
const DEFAULT_POSITION: NonNullable<StorefrontNotification['position']> =
  'top-right'

export type NotificationOptions = {
  duration?: number
  actions?: NotificationActionHandler[]
  type: NotificationComponent
  withProgress?: boolean
  position:
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
}

export type NotificationOnClickActions = {
  close: () => void
  [key: string]: () => void
}

export type NotificationActionHandler = {
  text: string
  class?: string
  href?: RouteLocationRaw
  onClick?: (actions: NotificationOnClickActions) => void
}

export type NotificationComponent = {
  classes: string
  iconComponent?: string
}

export type StorefrontNotification = {
  id: string
  message: string
  duration: number
  actions?: NotificationActionHandler[]
  withProgress: boolean
  position:
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
  type?: NotificationComponent & {
    iconClass?: string
    variant?: ToastType
  }
}

export function useNotification() {
  const notifications = useState<StorefrontNotification[]>(
    'notifications',
    () => [],
  )

  const show = (
    message: string,
    { duration, actions, type, withProgress, position }: NotificationOptions,
  ) => {
    notifications.value.push({
      message,
      id: `${nanoid()}-${Date.now()}`,
      duration: duration || DEFAULT_NOTIFICATION_DURATION,
      actions,
      type,
      position: position || DEFAULT_POSITION,
      withProgress,
    })
  }

  const close = (id: string) => {
    notifications.value = notifications.value.filter((item) => item.id !== id)
  }

  const closeAll = () => {
    notifications.value = []
  }

  return {
    notifications: computed(() => notifications.value),
    show,
    close,
    closeAll,
  }
}
